import { RawParams, StateOrName, StateService, TransitionOptions } from "@uirouter/angular";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, Input, OnInit } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { EMPTY, Observable, catchError, combineLatest, map, of, take, tap, zip } from "rxjs";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { localize } from "@gtmhub/localization";
import { BroadcastService } from "@webapp/core/broadcast/services/broadcast.service";
import { ClipboardService } from "@webapp/core/util/services/clipboard.service";
import { createErrorObject } from "@webapp/error-handling/error-util";
import { Kpi } from "@webapp/kpis/models/kpis.models";
import { KpiCommentsOnboardingService } from "@webapp/kpis/onboarding/kpi-comments-onboarding.service";
import { KPIS_DETAIL_TOTAL_STEPS, KpisOnboardingSteps } from "@webapp/kpis/onboarding/kpis-onboarding.models";
import { KpisOnboardingService } from "@webapp/kpis/onboarding/kpis-onboarding.service";
import { KpisFacade } from "@webapp/kpis/services/kpis-facade.service";
import { OnboardingComponent } from "@webapp/shared/components/onboarding/onboarding.component";
import { IOnboardingDefinition, IOnboardingJourneyState, OnboardingBaseActions } from "@webapp/shared/components/onboarding/onboarding.models";
import { DropdownMenuItem } from "@webapp/shared/dropdown/dropdown.models";
import { DropdownMenuItemBuilder } from "@webapp/shared/dropdown/util/dropdown-menu-item-builder";
import { ConfirmDeleteService } from "@webapp/shared/modal/confirm-delete.service";
import { UiDrawerRef } from "@webapp/ui/drawer/abstracts/drawer-ref";
import { KpiSnapshotModalService } from "../kpi-snapshot-form/kpi-snapshot-modal.service";
import { FirstCommentContext, KpiChartCommentData as KpiChartPointCommentData } from "./kpi-chart/kpi-chart.models";
import { KpiDetailsStateProxyService } from "./services/kpi-details-state-proxy.service";
import { KpiDetailsStateService } from "./services/kpi-details-state.service";

@UntilDestroy()
@Component({
  selector: "kpi-details",
  templateUrl: "./kpi-details.component.html",
  styleUrls: ["kpi-details.component.less"],
  providers: [KpiDetailsStateService, KpiDetailsStateProxyService],
})
export class KpiDetailsComponent implements OnInit {
  @Input()
  public kpiId: string;
  public isLoading$: Observable<boolean>;
  public kpi$: Observable<Kpi>;
  public kpiDetailsMenuItems: DropdownMenuItem[];
  public canEditKpi$: Observable<boolean>;
  public onboarding: IOnboardingDefinition;
  public commentsOnboarding: IOnboardingDefinition;
  public error$: Observable<{ title: string; message: string } | null>;
  public addFirstCommentToPointContext$: Observable<FirstCommentContext>;
  public kpiCommentsOnboardingAvailable$: Observable<boolean>;
  public chartPointCommentData: KpiChartPointCommentData;

  constructor(
    private kpiDetailsStateService: KpiDetailsStateService,
    private kpisFacade: KpisFacade,
    private broadcastService: BroadcastService,
    private kpisOnboardingService: KpisOnboardingService,
    private kpiCommentsOnboardingService: KpiCommentsOnboardingService,
    private kpiSnapshotModalService: KpiSnapshotModalService,
    private stateService: StateService,
    private clipboardService: ClipboardService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private confirmDeleteService: ConfirmDeleteService,
    private drawerRef: UiDrawerRef
  ) {}

  public ngOnInit(): void {
    this.kpiCommentsOnboardingAvailable$ = this.kpiDetailsStateService.isKpiCommentsOnboardingAvailable$();
    this.addFirstCommentToPointContext$ = this.kpiDetailsStateService.getAddFirstCommentToPointContext$();
    this.isLoading$ = this.kpiDetailsStateService.isLoading$();
    this.canEditKpi$ = this.kpiDetailsStateService.canEditKpi$();
    this.subscribeForKpisChanges();
    this.kpiDetailsStateService.init(this.kpiId);
    this.kpi$ = this.kpiDetailsStateService.getKpi$();
    this.initializeKpiDetailsOnboarding();
    this.initializeKpiCommentsOnboarding();
    this.kpiDetailsMenuItems = this.getMenuItems();
    this.handleError();
  }

  public hideAddFirstCommentView(): void {
    this.kpiDetailsStateService.hideAddFirstCommentView();
  }

  public close(): void {
    this.drawerRef.close();
  }

  public openSnapshotFormModal(): void {
    zip(this.kpiDetailsStateService.getKpi$(), this.kpiDetailsStateService.getSnapshots$())
      .pipe(take(1))
      .subscribe(([kpi, snapshots]) => {
        this.kpiSnapshotModalService
          .openKpiSnapshotModal(kpi, snapshots)
          .afterClose.pipe(take(1))
          .subscribe((isSnapshotUpdated) => {
            if (isSnapshotUpdated) {
              this.kpiDetailsStateService.fetchKpiSnapshots(kpi.id);
            }
          });
      });
  }

  public addCommentHandler(commentData: KpiChartPointCommentData): void {
    this.chartPointCommentData = commentData;
    this.kpiDetailsStateService.showAddFirstCommentView(commentData);
    if (this.stateService.includes("**.comment") || this.stateService.includes("**.comments")) {
      this.stateService.go("^");
    }
  }

  public get kpiHasNoSnapshotsAndProjections$(): Observable<boolean> {
    return combineLatest({ snapshots: this.kpiDetailsStateService.getSnapshots$(), projections: this.kpiDetailsStateService.getProjections$() }).pipe(
      untilDestroyed(this),
      map(({ snapshots, projections }) => snapshots.length === 0 && projections.length === 0)
    );
  }

  private handleError(): void {
    this.kpiDetailsStateService
      .getError$()
      .pipe(
        untilDestroyed(this),
        tap((errorResponse: HttpErrorResponse | null) => {
          if (errorResponse) {
            const { title, message } = createErrorObject(errorResponse);
            this.error$ = of({ title, message });
          } else {
            this.error$ = of(null);
          }
        })
      )
      .subscribe();
  }

  private initializeKpiDetailsOnboarding(): void {
    this.canEditKpi$.pipe(take(1)).subscribe((canEditKpi) => {
      this.kpisOnboardingService
        .getState$()
        .pipe(untilDestroyed(this))
        .subscribe((state: IOnboardingJourneyState) => {
          this.onboarding = {
            startingStep: KpisOnboardingSteps.KPIS_FILTER_STEP,
            totalSteps: canEditKpi ? KPIS_DETAIL_TOTAL_STEPS : KPIS_DETAIL_TOTAL_STEPS - 1,
            shouldShow: true,
          };

          const isOngoing = state.stage === "ONGOING";
          const isDisabled = state.stage === "DISABLED";
          const isEndedByUser = state.stage === "ENDED_BY_USER";

          if (isOngoing && state.currentStep >= KpisOnboardingSteps.KPIS_FILTER_STEP) {
            this.onboarding.shouldShow = true;
          } else if (state.currentStep < KpisOnboardingSteps.KPIS_FILTER_STEP || isDisabled || isEndedByUser) {
            this.onboarding.shouldShow = false;
          }

          this.onboarding.delegate = {
            shouldSeeOnboarding: (): boolean => this.onboarding.shouldShow,
            getRedirectStateForStep: (step): { to: StateOrName; params?: RawParams; options?: TransitionOptions } => {
              if (step === 1 && state.currentStep === 2) {
                this.kpisOnboardingService.back({ inTransition: false });

                return { to: "gtmhub.kpis.details" };
              }
              return null;
            },
            journeyCompleted: () => this.kpisOnboardingService.complete(),
            journeyClosed: () => this.kpisOnboardingService.close(),
          };
        });
    });
  }

  private initializeKpiCommentsOnboarding(): void {
    this.canEditKpi$.pipe(take(1)).subscribe((canEditKpi: boolean) => {
      if (!canEditKpi) {
        return;
      }

      this.initializeKpiCommentsOnboardingState();
    });
  }

  private initializeKpiCommentsOnboardingState(): void {
    this.kpiCommentsOnboardingService
      .getState$()
      .pipe(untilDestroyed(this))
      .subscribe((state: IOnboardingJourneyState) => {
        const delegate = {
          shouldSeeOnboarding: (): boolean => this.commentsOnboarding.shouldShow,
          journeyCompleted: () => this.kpiCommentsOnboardingService.complete(),
          journeyClosed: () => this.kpiCommentsOnboardingService.close(),
          getRedirectStateForStep: () => null,
          getPrimaryActionForStep: (step, baseActions: Partial<Pick<OnboardingComponent, OnboardingBaseActions>>) => ({
            primaryCTAHandler: (): void => {
              baseActions.gotIt();
            },
            primaryCTA: localize("got_it"),
          }),
        };

        this.commentsOnboarding = {
          startingStep: 1,
          totalSteps: 1,
          shouldShow: true,
          delegate,
        };

        const isDisabled = state.stage === "DISABLED";
        const isEndedByUser = state.stage === "ENDED_BY_USER";

        if (isDisabled || isEndedByUser || !this.kpiCommentsOnboardingService.shouldSeeJourneyAccordingToMetadata()) {
          this.commentsOnboarding.shouldShow = false;
        }

        if (this.kpiCommentsOnboardingService.shouldSeeJourneyAccordingToMetadata()) {
          this.kpiCommentsOnboardingService.markAsShown();
        }
      });
  }

  private getMenuItems(): DropdownMenuItem[] {
    const menuItemBuilder = new DropdownMenuItemBuilder();

    const editItem = menuItemBuilder
      .setKey("edit")
      .setAction({ state: "gtmhub.kpis._singleKpi.form", params: { kpiId: this.kpiId } })
      .setIsVisibleCondition(this.kpiDetailsStateService.canEditKpi$())
      .build();
    const copyLinkItem = menuItemBuilder
      .setKey("share_link")
      .setTrack("Generated sharable link")
      .setTrackMetaData({ from: "kpi details modal", entity_type: "kpi", entity_id: this.kpiId })
      .setAction({ handler: (): void => this.copyLinkToClipboard() })
      .build();
    const deleteItem = menuItemBuilder
      .setKey("delete")
      .setToBeDestructive()
      .setAction({ handler: (): void => this.openConfirmDeleteModal() })
      .setIsVisibleCondition(this.kpiDetailsStateService.canDeleteKpi$())
      .build();
    return [editItem, copyLinkItem, deleteItem];
  }

  private copyLinkToClipboard(): void {
    this.clipboardService.copyToClipboard(this.getLinkUrl(this.kpiId));
  }

  private getLinkUrl(kpiId: string): string {
    return this.stateService.href("gtmhub.kpis.details", { kpiId }, { absolute: true });
  }

  private openConfirmDeleteModal(): void {
    this.kpi$.pipe(take(1)).subscribe((kpi) => {
      this.confirmDeleteService.confirmDeleteItem({ itemKey: "kpi" }).subscribe(() => this.deleteKpi(kpi));
    });
  }

  private deleteKpi(kpi: Kpi): void {
    this.kpisFacade
      .delete$(kpi.id)
      .pipe(
        take(1),
        catchError((error: HttpErrorResponse) => {
          this.uiErrorHandlingService.handleModal(createErrorObject(error));

          return EMPTY;
        })
      )
      .subscribe(() => {
        // to do: refactor with https://quantive-inc.atlassian.net/browse/GVS-33860
        // the kpiDeleted event is consumed by the KPIs table that has some complicated logic related to KPI ordering in user settings
        this.broadcastService.emit("kpiDeleted", kpi.id);
        this.stateService.go("gtmhub.kpis");
      });
  }

  // to do: handle changes via an observable service - edit, delete, create snapshot in both directions - https://quantive-inc.atlassian.net/browse/GVS-33860
  // to do: on kpiUpdated we need to fetch snapshots if the aggregation or target operator is changed
  private subscribeForKpisChanges(): void {
    this.broadcastService
      .on("kpiUpdated")
      .pipe(untilDestroyed(this))
      .subscribe((kpi: Kpi) => {
        // the check below is not needed with the current state setup(form is always opened on top of details), adding this if we change it the future
        if (this.kpiId === kpi.id) {
          this.kpiDetailsStateService.fetchKpi(this.kpiId);
        }
      });

    // to do: handle changes via an observable service - edit, delete, create snapshot in both directions - https://quantive-inc.atlassian.net/browse/GVS-33860
    this.broadcastService
      .on("kpiSnapshotCreatedViaTable")
      .pipe(untilDestroyed(this))
      .subscribe((kpiId: string) => {
        if (this.kpiId === kpiId) {
          this.kpiDetailsStateService.fetchKpiSnapshots(this.kpiId);
        }
      });
  }
}
